import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import Flex from "../../../../../components/Flex";
import StyledDialog from "../../../../../components/StyledDialog";
import { NEW_PATIENT_APPOINTMENT_SCHEMA } from "../../../../../constants/scheduling";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  NEW_APPOINTMENT_INITIAL_VALUES,
  NewAppointmentType,
} from "../../../../../pages/app/provider/scheduling/appointment/appointment-constant";
import { useAddAppointment } from "../../../../../queries/use-add-appointment";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import { NewPatientPortalAppointmentForm } from "./NewPatientPortalAppointmentForm";

interface PatientPortalAppointMentModalProps {
  disclosureHost: DisclosureHost;
}

const PatientPortalAppointMentModal = ({
  disclosureHost,
}: PatientPortalAppointMentModalProps) => {
  const { mutateAsync: addAppointment } = useAddAppointment();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const [me] = useCurrentUser();

  const handleSubmit = (values: any) => {
    const timezone = values?.timezone?.curType;
    const customForms = values?.customForms
      ? values?.customForms?.map((it: any) => it?.value)?.join(",")
      : null;
    const assessments = values?.assessments
      ?.map((it: any) => it?.value)
      ?.join(",");

    const payload = {
      ...values,
      type: values?.type?.label,
      customForms: customForms,
      primary_insurance: undefined,
      secondary_insurance: undefined,
      patientPrimaryInsurance: values?.primary_insurance?.uuid
        ? values?.primary_insurance
        : undefined,
      patientSecondaryInsurance: values?.secondary_insurance?.uuid
        ? values?.secondary_insurance
        : undefined,
      timezone,
      locationId: values?.locationId?.value,
      patientId: patient?.uuid as any,
      assessments,
      providerId: values.providerId?.value!,
      reminders: values.reminder_set ? values?.reminders : undefined,
      providerName: me?.firstName + " " + me?.lastName,
      providerRole: me?.role,
      appointmentRequest: true,
      xTENANTID: getXtenantId(),
    };

    addAppointment(payload)
      .then((res: any) => {
        queryClient.invalidateQueries({ queryKey: ["appointment-list"] });
        queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
        queryClient.invalidateQueries({
          queryKey: ["patient-chart-forms-list"],
        });
        showSnackbar(getSuccessSnackbar(res));
        closeModal();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const [patient] = useCurrentUser();

  const formik = useFormik<NewAppointmentType>({
    initialValues: {
      ...NEW_APPOINTMENT_INITIAL_VALUES,
      providerId: disclosureHost.data,
    },
    validationSchema: NEW_PATIENT_APPOINTMENT_SCHEMA,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  function closeModal() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={closeModal}
      dialogTitle="Book Appointment"
      dialogAction={
        <Flex
          width={"100%"}
          justifyContent={"end"}
          gap={3}
          alignItems={"center"}
        >
          <Flex gap={2}>
            <Button variant="outlined" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => formik.handleSubmit()}>
              Save And Close
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Flex flexDirection={"column"} gap={2} mb={2} width={"750px"}>
        <NewPatientPortalAppointmentForm formik={formik} />
      </Flex>
    </StyledDialog>
  );
};

export default PatientPortalAppointMentModal;
